/**
 * The recurring signup sheet write path's wire contracts (plan
 * 2026-09-18-0147, U3 — R1, R4, R5, R6, R9, R10, R18).
 *
 * Two endpoints share ONE request body: `PATCH /:eventPublicId/recurrence`
 * saves it, `POST /:eventPublicId/recurrence/preview` runs the identical
 * validation and diff in dry run (KTD10). That is why the request schema lives
 * here once rather than beside either handler — the organizer's change summary
 * can only be trusted if the preview cannot take a body the save would refuse.
 *
 * Both responses carry the SAME `changes` summary, computed from the same
 * series diff. The gray-box suite asserts the preview's summary equals the
 * save's, which only holds because neither side has its own shape to drift in.
 *
 * Instants travel as ISO strings; the rules themselves carry school-local
 * wall-clock strings — see `schemas/entities/signup-sheet-rule.ts` for why.
 */

import { z } from 'zod'
import { TicketedEventAdmissionStatusSchema } from '../../schemas/entities/ticketed-event-admission'
import {
  ParticipantKindSchema,
  SIGNUP_RULE_LIMITS,
  SignupCloseRuleSchema,
  SignupRepeatRuleSchema,
  SlotStateSchema
} from '../../schemas/entities/signup-sheet-rule'

/**
 * The body both endpoints take.
 *
 * `perRegistrantCap` and `horizonDays` are optional because the rule carries
 * the organizer's own horizon and the cap has a product default; an omitted
 * field means "use the default", never "clear it".
 */
export const SetSignupRecurrenceRequestSchema = z.object({
  repeatRule: SignupRepeatRuleSchema,
  closeRule: SignupCloseRuleSchema,
  participantKind: ParticipantKindSchema,
  /** Spots one registrant email may hold across the visible horizon (R9). */
  perRegistrantCap: z.number().int().min(1).optional(),
  /**
   * How far ahead dates show (R2). Accepted at the top level as well as inside
   * the repeat rule because the organizer's form edits it beside the cap; the
   * top-level value wins when both are present.
   */
  horizonDays: z.number().int().min(1).max(SIGNUP_RULE_LIMITS.MAX_HORIZON_DAYS).optional()
})
export type SetSignupRecurrenceRequest = z.infer<typeof SetSignupRecurrenceRequestSchema>

/** One slot as the change summary and the date preview name it. */
export const SignupRecurrenceSlotSchema = z.object({
  /** `TicketType.generatedKey` — `20261126T1515` (KTD2). */
  generatedKey: z.string(),
  /** The formatted local date and time the slot is named after (KTD15). */
  name: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  cutoffAt: z.string().nullable(),
  capacity: z.number().int()
})
export type SignupRecurrenceSlot = z.infer<typeof SignupRecurrenceSlotSchema>

/** Which of a slot's rule-owned values a save would move. */
export const SignupSlotChangeSchema = z.enum(['startsAt', 'endsAt', 'cutoffAt', 'capacity'])
export type SignupSlotChange = z.infer<typeof SignupSlotChangeSchema>

/** Why a slot was left exactly as it is (R18). */
export const SignupSlotKeepReasonSchema = z.enum(['has_registrations', 'edited', 'cancelled'])
export type SignupSlotKeepReason = z.infer<typeof SignupSlotKeepReasonSchema>

/**
 * What a save would do, or has just done, to the slots that already exist.
 *
 * Each arm carries both a count and the slots themselves: the dashboard shows
 * the counts in a sentence and the slots in a list, and AE16 needs the kept,
 * registered January date NAMED so the organizer can go cancel it under R19.
 */
export const SignupRecurrenceChangesSchema = z.object({
  /** Keys the series wants that do not exist yet, inside the horizon. */
  mint: z.object({ count: z.number().int(), slots: z.array(SignupRecurrenceSlotSchema) }),
  /** Unstamped slots whose rule-owned values move. */
  update: z.object({
    count: z.number().int(),
    slots: z.array(SignupRecurrenceSlotSchema.extend({ changes: z.array(SignupSlotChangeSchema) }))
  }),
  /** Unstamped, unreferenced slots whose key has left the series. */
  remove: z.object({ count: z.number().int(), slots: z.array(SignupRecurrenceSlotSchema) }),
  /**
   * Slots the save will not touch. `inSeries` false is AE16's case: the date
   * has left the rule but the slot has registrations, so it stays until the
   * organizer cancels it.
   */
  keep: z.object({
    count: z.number().int(),
    slots: z.array(SignupRecurrenceSlotSchema.extend({ reason: SignupSlotKeepReasonSchema, inSeries: z.boolean() }))
  }),
  /** Present, in series, and already correct. Count only — nothing to show. */
  unchanged: z.object({ count: z.number().int() })
})
export type SignupRecurrenceChanges = z.infer<typeof SignupRecurrenceChangesSchema>

/** Where the series stops once exclusions are accounted for (KTD4, AE20). */
export const SignupResolvedEndSchema = z.object({
  /** The last school-local date the rule counts, `YYYY-MM-DD`. */
  lastDate: z.string(),
  /** The end instant of the last window on that date — `repeatEndsAt` (KTD9). */
  endsAt: z.string(),
  /** How many dates the rule counts in total. Exclusions are not counted. */
  countedDates: z.number().int()
})
export type SignupResolvedEnd = z.infer<typeof SignupResolvedEndSchema>

/** The rolling window registrants can currently see (R2). */
export const SignupHorizonSchema = z.object({
  days: z.number().int(),
  /** End of the last school-local day inside the horizon. */
  endsAt: z.string()
})
export type SignupHorizon = z.infer<typeof SignupHorizonSchema>

/**
 * The preview's dates, grouped by month (R18).
 *
 * Grouped rather than flat because a rule running to June generates hundreds of
 * dates and the organizer reviews them a month at a time. `inHorizon` is what
 * tells them which of those dates exist as slots today and which the horizon
 * has yet to reach.
 */
export const SignupPreviewMonthSchema = z.object({
  /** `YYYY-MM`. */
  month: z.string(),
  dates: z.array(
    z.object({
      date: z.string(),
      inHorizon: z.boolean(),
      slots: z.array(SignupRecurrenceSlotSchema)
    })
  )
})
export type SignupPreviewMonth = z.infer<typeof SignupPreviewMonthSchema>

/** `POST /:eventPublicId/recurrence/preview` — dry run over the WHOLE series. */
export const PreviewSignupRecurrenceResponseSchema = z.object({
  timeZone: z.string(),
  resolvedEnd: SignupResolvedEndSchema,
  horizon: SignupHorizonSchema,
  /** Dates the rule counts from today to its resolved end. */
  totalDates: z.number().int(),
  /** Slots those dates generate — dates times windows. */
  totalSlots: z.number().int(),
  months: z.array(SignupPreviewMonthSchema),
  changes: SignupRecurrenceChangesSchema
})
export type PreviewSignupRecurrenceResponse = z.infer<typeof PreviewSignupRecurrenceResponseSchema>

/**
 * `PATCH /:eventPublicId/recurrence` — what the save actually did.
 *
 * `changes` is the same summary the preview returned for the same body, and
 * `applied` is what the transaction managed against it. They differ only where
 * a concurrent writer intervened: a slot that gained a registration between the
 * diff and the write drops out of `removed`, and a key a parallel save already
 * minted drops out of `minted` (the unique key on `(event, generatedKey)` is
 * what makes that a no-op rather than a duplicate).
 */
export const SetSignupRecurrenceResponseSchema = z.object({
  timeZone: z.string(),
  resolvedEnd: SignupResolvedEndSchema,
  horizon: SignupHorizonSchema,
  changes: SignupRecurrenceChangesSchema,
  applied: z.object({
    minted: z.number().int(),
    updated: z.number().int(),
    removed: z.number().int(),
    /** Removals that had voided or cancelled rows pointing at them (KTD11). */
    softHidden: z.number().int()
  }),
  /** The sheet's next unpassed slot start, or null once the series is spent. */
  startsAt: z.string().nullable(),
  repeatEndsAt: z.string()
})
export type SetSignupRecurrenceResponse = z.infer<typeof SetSignupRecurrenceResponseSchema>

// ============================================================================
// Slot operations (U7 — R19, R20; KTD12)
// ============================================================================

/**
 * `POST /:eventPublicId/slots/:ticketTypePublicId/cancel` — cancel one date.
 *
 * The reason is OPTIONAL and INTERNAL: it is recorded on the slot and on each
 * voided spot for the dashboard and the export, and it never reaches the
 * registrant notice, which names only the sheet and the date (R19).
 */
export const CancelSignupSlotRequestSchema = z.object({
  reason: z.string().trim().min(1).max(1000).optional()
})
export type CancelSignupSlotRequest = z.infer<typeof CancelSignupSlotRequestSchema>

export const CancelSignupSlotResponseSchema = z.object({
  ticketTypePublicId: z.string(),
  /** True when the date was already cancelled: nothing was voided and nothing sent. */
  alreadyCancelled: z.boolean(),
  cancelledAt: z.string(),
  /** Spots this call voided. */
  voidedAdmissions: z.number().int(),
  /** Distinct registrant addresses a notice was queued for. */
  noticeRecipients: z.number().int(),
  /**
   * False when no Customer.io identifier is recorded for the slot-cancelled
   * notice (KTD12). The cancel still stands; the dashboard's confirmation has
   * to tell the organizer that nobody will hear.
   */
  noticeConfigured: z.boolean()
})
export type CancelSignupSlotResponse = z.infer<typeof CancelSignupSlotResponseSchema>

/**
 * `POST /:eventPublicId/admissions/:admissionPublicId/cancel` — free one spot
 * (R20). No notice is sent.
 */
export const CancelSignupRegistrationRequestSchema = z.object({
  reason: z.string().trim().min(1).max(1000).optional()
})
export type CancelSignupRegistrationRequest = z.infer<typeof CancelSignupRegistrationRequestSchema>

export const CancelSignupRegistrationResponseSchema = z.object({
  admissionPublicId: z.string(),
  /** The slot the spot was on, so the dashboard knows which row to refresh. */
  ticketTypePublicId: z.string(),
  /** False when the spot had already left the live set — a repeat cancel frees nothing. */
  cancelled: z.boolean(),
  /** The slot's spots left once the repair has run. */
  capacityRemaining: z.number().int()
})
export type CancelSignupRegistrationResponse = z.infer<typeof CancelSignupRegistrationResponseSchema>

// ============================================================================
// Reporting (U11 — R17; AE11, AE18; KTD14)
// ============================================================================

/**
 * ONE registration on a slot — one admission row, as the organizer reads it.
 *
 * The grain is deliberately the SEAT, not the order (KTD14): the question a
 * signup sheet is asked is "who is coming Friday", and a parent who took two
 * spots for two children is two answers to it. The order-shaped list beside
 * this one is untouched and stays the right read for a ticketed event.
 *
 * VOIDED AND CANCELLED ROWS ARE INCLUDED. After a date is cancelled (AE11) the
 * organizer still has to be able to see, and export, the twelve families who
 * had signed up — a list that quietly dropped them would make the cancellation
 * unauditable. `status` is what separates them from the live ones.
 *
 * The PARTICIPANT is the admission's holder — on a child sheet, the child's
 * name the form collected per seat (KTD6). The REGISTRANT is the person who
 * placed the order. A seat an admin issued directly has no order, so its own
 * holder details stand in for the registrant; that is also what makes a
 * registrant-kind sheet read as "the buyer signed themselves up".
 */
export const SlotRegistrationSchema = z.object({
  admissionPublicId: z.string(),
  participantFirstName: z.string().nullable(),
  participantLastName: z.string().nullable(),
  registrantFirstName: z.string().nullable(),
  registrantLastName: z.string().nullable(),
  registrantEmail: z.string().nullable(),
  status: TicketedEventAdmissionStatusSchema,
  /** The reason recorded for the CURRENT status — a cancelled date's reason reaches the organizer here (R19). */
  statusReason: z.string().nullable(),
  /** The order's reference code, or null for a directly issued spot. */
  referenceCode: z.string().nullable(),
  createdAt: z.string(),
  voidedAt: z.string().nullable(),
  cancelledAt: z.string().nullable()
})
export type SlotRegistration = z.infer<typeof SlotRegistrationSchema>

/**
 * `GET /:eventPublicId/slots/:ticketTypePublicId/registrations` — one date's
 * registrations (R17, AE11, AE18).
 *
 * The slot's own state travels with the rows so the dashboard's per-date panel
 * can title itself ("Cancelled — gym flooded") without a second read, and a
 * PAST slot answers exactly like a visible one: the organizer keeps every date
 * readable after the sheet has ended (AE18).
 */
export const ListSlotRegistrationsResponseSchema = z.object({
  ticketTypePublicId: z.string(),
  /** The slot's name — its formatted local date and time (KTD15). */
  name: z.string(),
  slotStartsAt: z.string().nullable(),
  slotEndsAt: z.string().nullable(),
  slotState: SlotStateSchema,
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  capacity: z.number().int(),
  capacityRemaining: z.number().int(),
  /** How many of the rows below still hold a spot — the count the organizer plans against. */
  liveCount: z.number().int(),
  registrations: z.array(SlotRegistrationSchema)
})
export type ListSlotRegistrationsResponse = z.infer<typeof ListSlotRegistrationsResponseSchema>

/**
 * The registrations CSV's columns are NOT declared here.
 *
 * They live beside the writer, in
 * `services/ticketed-events/slots/export-registrations-csv.ts`, which builds
 * both the header line and every row from that one list. Nothing outside the
 * backend consumes them at runtime — the dashboard downloads the file, it does
 * not parse it — and a second declaration on the wire would be a list that can
 * disagree with the file it describes. The route's JSDoc states the order for
 * anyone reading the API surface.
 */
